import React from "react";
import PlacesAutocomplete from "react-places-autocomplete";
import { useNavigate } from "react-router-dom";
import {
  FormControl,
  Select,
  MenuItem,
  Avatar,
  Grid,
  TextField,
  Button,
  Container,
  Typography,
  Box,
  Chip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import "react-dates/initialize";
import "react-dates/lib/css/_datepicker.css";
import { SingleDatePicker } from "react-dates";
import LocationCityIcon from "@material-ui/icons/LocationCity";
import AddPhotoAlternateOutlinedIcon from "@material-ui/icons/AddPhotoAlternateOutlined";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import { Add, Cancel } from "@material-ui/icons";
import { Formik } from "formik";
import * as Yup from "yup";
import moment from "moment";
import { connect } from "react-redux";
import { editExperience } from "src/actions/ExperienceActions";
import { options, COUNTRIES } from "src/utils/misc";
import MultiItemsInput from "src/components/multipleInputField/MultipleInputField";

// ─── Design tokens ─────────────────────────────────────────────────────────
const TOKENS = {
  bg: "#F5F6FA",
  surface: "#FFFFFF",
  border: "#EEF0F5",
  borderStrong: "#E5E7EB",
  textPrimary: "#0F172A",
  textSecondary: "#64748B",
  textMuted: "#94A3B8",
  accent: "#222222",
  accentDark: "#000000",
  divider: "#F1F5F9",
};

const useStyles = makeStyles((theme) => ({
  page: {
    backgroundColor: TOKENS.bg,
    minHeight: "100%",
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(8),
  },
  container: { maxWidth: 1100 },

  // Header
  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  pageTitle: {
    fontSize: 26,
    fontWeight: 700,
    color: TOKENS.textPrimary,
    lineHeight: 1.2,
    marginBottom: 4,
  },
  pageSubtitle: { fontSize: 14, color: TOKENS.textSecondary },
  headerActions: {
    display: "flex",
    gap: theme.spacing(1.5),
    flexWrap: "wrap",
  },
  cancelBtn: {
    borderColor: TOKENS.borderStrong,
    color: TOKENS.textPrimary,
    backgroundColor: TOKENS.surface,
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 10,
    padding: "8px 20px",
    "&:hover": {
      backgroundColor: TOKENS.bg,
      borderColor: TOKENS.borderStrong,
    },
  },
  primaryBtn: {
    backgroundColor: TOKENS.accent,
    color: "#FFFFFF",
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 10,
    padding: "8px 24px",
    boxShadow: "none",
    "&:hover": { backgroundColor: TOKENS.accentDark, boxShadow: "none" },
    "&:disabled": { backgroundColor: "#A5B4FC", color: "#FFFFFF" },
  },

  // Status banner
  statusBanner: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "12px 16px",
    borderRadius: 12,
    marginBottom: theme.spacing(3),
    fontSize: 14,
    fontWeight: 500,
  },
  statusApproved: { backgroundColor: "#ECFDF5", color: "#059669" },
  statusPending: { backgroundColor: "#FEF3C7", color: "#B45309" },

  // Section card
  sectionCard: {
    backgroundColor: TOKENS.surface,
    border: `1px solid ${TOKENS.border}`,
    borderRadius: 16,
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: TOKENS.textPrimary,
  },
  sectionSubtitle: {
    fontSize: 13,
    color: TOKENS.textSecondary,
    marginTop: 4,
    marginBottom: theme.spacing(3),
  },
  fieldLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: TOKENS.textPrimary,
    marginBottom: 6,
    display: "block",
  },
  fieldHelper: {
    fontSize: 12,
    color: TOKENS.textMuted,
    marginTop: 4,
  },

  // Outlined input look
  textField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      backgroundColor: TOKENS.surface,
      fontSize: 14,
      "& fieldset": { borderColor: TOKENS.borderStrong },
      "&:hover fieldset": { borderColor: TOKENS.textMuted },
      "&.Mui-focused fieldset": {
        borderColor: TOKENS.accent,
        borderWidth: 1,
      },
    },
    "& .MuiOutlinedInput-input": { padding: "12px 14px" },
  },
  selectRoot: {
    borderRadius: 10,
    backgroundColor: TOKENS.surface,
    fontSize: 14,
    "& .MuiOutlinedInput-notchedOutline": { borderColor: TOKENS.borderStrong },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: TOKENS.textMuted,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: TOKENS.accent,
      borderWidth: 1,
    },
  },
  selectInput: { padding: "12px 14px" },

  // Image gallery
  imageGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
    gap: theme.spacing(2),
  },
  imageThumb: {
    position: "relative",
    aspectRatio: "1 / 1",
    borderRadius: 12,
    overflow: "hidden",
    backgroundColor: TOKENS.bg,
    cursor: "pointer",
    transition: "transform 0.2s ease",
    "&:hover": { transform: "translateY(-2px)" },
  },
  imageThumbImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  coverBadge: {
    position: "absolute",
    bottom: 8,
    left: 8,
    backgroundColor: TOKENS.accent,
    color: "#FFFFFF",
    fontSize: 11,
    fontWeight: 600,
    padding: "4px 10px",
    borderRadius: 999,
  },
  addImageBtn: {
    aspectRatio: "1 / 1",
    border: `2px dashed ${TOKENS.borderStrong}`,
    borderRadius: 12,
    backgroundColor: TOKENS.bg,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: TOKENS.textSecondary,
    transition: "all 0.2s ease",
    textDecoration: "none",
    "&:hover": {
      borderColor: TOKENS.accent,
      color: TOKENS.accent,
    },
  },
  addImageLabel: { fontSize: 12, fontWeight: 600, marginTop: 8 },

  // PlacesAutocomplete
  placesWrap: { position: "relative" },
  placesInput: {
    width: "100%",
    height: 44,
    padding: "0 14px",
    fontSize: 14,
    fontFamily: "inherit",
    border: `1px solid ${TOKENS.borderStrong}`,
    borderRadius: 10,
    backgroundColor: TOKENS.surface,
    color: TOKENS.textPrimary,
    outline: "none",
    "&:focus": { borderColor: TOKENS.accent },
  },
  suggestionsList: {
    position: "absolute",
    top: "calc(100% + 4px)",
    left: 0,
    right: 0,
    backgroundColor: TOKENS.surface,
    border: `1px solid ${TOKENS.border}`,
    borderRadius: 10,
    boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
    zIndex: 10,
    overflow: "hidden",
  },
  suggestion: {
    display: "flex",
    alignItems: "center",
    padding: "10px 14px",
    cursor: "pointer",
    fontSize: 14,
    color: TOKENS.textPrimary,
    transition: "background 0.15s ease",
  },

  // Variant card
  variantCard: {
    border: `1px solid ${TOKENS.border}`,
    borderRadius: 12,
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    backgroundColor: "#FAFBFC",
  },
  variantHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(1.5),
  },
  variantLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: TOKENS.textSecondary,
  },
  removeVariantBtn: {
    color: "#EF4444",
    minWidth: "auto",
    padding: "4px 8px",
    textTransform: "none",
    fontSize: 12,
    fontWeight: 600,
    "&:hover": { backgroundColor: "rgba(239,68,68,0.06)" },
  },
  addVariantBtn: {
    marginTop: theme.spacing(2),
    borderColor: TOKENS.accent,
    color: TOKENS.accent,
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 10,
    "&:hover": {
      borderColor: TOKENS.accent,
      backgroundColor: "rgba(34, 34, 34,0.04)",
    },
  },

  // Sticky save bar
  stickyBar: {
    position: "sticky",
    bottom: theme.spacing(2),
    marginTop: theme.spacing(3),
    backgroundColor: TOKENS.surface,
    border: `1px solid ${TOKENS.border}`,
    borderRadius: 14,
    padding: "12px 16px",
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1.5),
    boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
    zIndex: 5,
  },
}));

// ─── Helpers ───────────────────────────────────────────────────────────────
const getItemName = (itemsList) => itemsList?.map((item) => item.name) || [];

// FormField: label above input — matches the admin reference pattern
const FormField = ({ label, helper, children, classes }) => (
  <Box>
    {label && <Typography className={classes.fieldLabel}>{label}</Typography>}
    {children}
    {helper && (
      <Typography className={classes.fieldHelper}>{helper}</Typography>
    )}
  </Box>
);

// ─── Component ─────────────────────────────────────────────────────────────
function EditExperience({ editExperience, experience }) {
  const classes = useStyles();
  const navigate = useNavigate();

  const [formState, setFormState] = React.useState({
    experience_category: "Art and design",
    title: "",
    country: "",
    district: "",
    brief_description: "",
    description: "",
    what_guests_need_to_know: "",
    price: 0.0,
    minGroupSize: 1,
    maxGroupSize: 1000,
    dates: [],
    bookedDates: [],
    calendarFocused: false,
  });
  const [address, setLocation] = React.useState(`${experience?.address || ""}`);
  const [variant, setVariant] = React.useState(
    experience?.price_variants?.length > 0
      ? experience?.price_variants
      : [
          {
            category: "Adult",
            number_of_people: 0,
            price: experience?.price,
          },
        ],
  );
  const [languages, setLanguages] = React.useState(
    getItemName(experience?.language),
  );
  const [currentLanguage, setCurrentLanguage] = React.useState("");
  const [provisions, setProvisions] = React.useState(
    getItemName(experience?.provisions),
  );
  const [currentProvision, setCurrentProvision] = React.useState("");

  const handleAddProvision = () => {
    if (
      currentProvision.trim() &&
      !provisions.includes(currentProvision.trim())
    ) {
      setProvisions([...provisions, currentProvision.trim()]);
      setCurrentProvision("");
    }
  };
  const handleDeleteProvision = (p) =>
    setProvisions(provisions.filter((x) => x !== p));

  const handleAddLanguage = () => {
    if (currentLanguage.trim() && !languages.includes(currentLanguage.trim())) {
      setLanguages([...languages, currentLanguage.trim()]);
      setCurrentLanguage("");
    }
  };
  const handleDeleteLanguage = (l) =>
    setLanguages(languages.filter((x) => x !== l));

  const handleDateChange = (date) => {
    const isPresent = formState.dates.find((d) => date.isSame(d));
    setFormState({
      ...formState,
      dates: formState.dates
        .filter((d) => (isPresent ? !date.isSame(d) : true))
        .concat(isPresent ? [] : [date])
        .sort((d1, d2) => (d1.isBefore(d2) ? -1 : 1)),
      bookedDates: formState.dates
        .filter((d) => (isPresent ? !date.isSame(d) : true))
        .concat(isPresent ? [] : [date])
        .sort((d1, d2) => (d1.isBefore(d2) ? -1 : 1)),
    });
  };

  React.useEffect(() => {
    setFormState((prev) => ({
      ...prev,
      dates:
        (experience?.availableDates &&
          experience?.availableDates.map((d) => moment(d))) ||
        [],
      bookedDates: experience?.availableDates || [],
      ...experience,
    }));
    setLanguages(getItemName(experience?.language));
    setProvisions(getItemName(experience?.provisions));
    setLocation(`${experience?.address || ""}`);
    // eslint-disable-next-line
  }, [experience]);

  const handleChangeVariant = (i, e) => {
    const newVariant = [...variant];
    newVariant[i][e.target.name] = e.target.value;
    setVariant(newVariant);
  };
  const addFormFields = () =>
    setVariant([...variant, { category: "", price: 0, number_of_people: 0 }]);
  const removeFormFields = (i) => {
    const newVariant = [...variant];
    newVariant.splice(i, 1);
    setVariant(newVariant);
  };

  const subtitle = [experience?.country, experience?.district]
    .filter(Boolean)
    .join(", ");

  return (
    <Box className={classes.page}>
      <Container className={classes.container} maxWidth={false}>
        <Formik
          enableReinitialize
          initialValues={experience || {}}
          validationSchema={Yup.object().shape({
            title: Yup.string(),
            district: Yup.string(),
            brief_description: Yup.string(),
            description: Yup.string(),
            what_guests_need_to_know: Yup.string(),
            price: Yup.number(),
            minGroupSize: Yup.number(),
            maxGroupSize: Yup.number(),
          })}
          onSubmit={async (values) => {
            const provisions_data = provisions.map((p) => ({ name: p }));
            const languages_data = languages.map((l) => ({ name: l }));
            const price_variants =
              variant.length > 1
                ? variant
                : [
                    {
                      category: "Adult",
                      price: values.price,
                      number_of_people: 1,
                    },
                  ];
            const obj = {
              ...values,
              experience_category: formState.experience_category,
              address,
              price_variants,
              language: languages_data,
              provisions: provisions_data,
              country: formState.country,
            };
            await editExperience(obj, experience?.id, navigate);
          }}
        >
          {({
            errors,
            handleBlur,
            handleChange,
            handleSubmit,
            isSubmitting,
            touched,
            values,
          }) => (
            <form onSubmit={handleSubmit}>
              {/* ─── Header ─────────────────────────────────────────── */}
              <Box className={classes.header}>
                <Box>
                  <Typography className={classes.pageTitle}>
                    Edit Experience
                  </Typography>
                  <Typography className={classes.pageSubtitle}>
                    {experience?.title || "Untitled experience"}
                    {subtitle && <> — {subtitle}</>}
                  </Typography>
                </Box>
                <Box className={classes.headerActions}>
                  <Button
                    variant="outlined"
                    className={classes.cancelBtn}
                    onClick={() => navigate(-1)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className={classes.primaryBtn}
                    disabled={isSubmitting}
                  >
                    Save Changes
                  </Button>
                </Box>
              </Box>

              {/* ─── Status banner ───────────────────────────────────── */}
              <Box
                className={`${classes.statusBanner} ${
                  experience?.approved
                    ? classes.statusApproved
                    : classes.statusPending
                }`}
              >
                {experience?.approved ? (
                  <CheckCircleOutlineIcon style={{ fontSize: 18 }} />
                ) : (
                  <AccessTimeIcon style={{ fontSize: 18 }} />
                )}
                {experience?.approved
                  ? "This experience is approved and visible to guests."
                  : "This experience is pending review. It will go live once approved."}
              </Box>

              {/* ─── Images ──────────────────────────────────────────── */}
              <Box className={classes.sectionCard}>
                <Typography className={classes.sectionTitle}>Images</Typography>
                <Typography className={classes.sectionSubtitle}>
                  Cover image and gallery photos. First image is used as the
                  cover.
                </Typography>
                <Box className={classes.imageGrid}>
                  {experience?.coverImage_URL && (
                    <Box className={classes.imageThumb}>
                      <img
                        src={experience.coverImage_URL}
                        alt="cover"
                        className={classes.imageThumbImg}
                      />
                      <span className={classes.coverBadge}>Cover</span>
                    </Box>
                  )}
                  {experience?.other_images?.map((image, idx) => (
                    <Box key={idx} className={classes.imageThumb}>
                      <img
                        src={image.image_url}
                        alt={`gallery-${idx}`}
                        className={classes.imageThumbImg}
                      />
                    </Box>
                  ))}
                  <Box
                    className={classes.addImageBtn}
                    onClick={() => navigate("/app/experience/updateImages")}
                  >
                    <AddPhotoAlternateOutlinedIcon style={{ fontSize: 28 }} />
                    <Typography className={classes.addImageLabel}>
                      Update images
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* ─── Basic Information ───────────────────────────────── */}
              <Box className={classes.sectionCard}>
                <Typography className={classes.sectionTitle}>
                  Basic Information
                </Typography>
                <Typography className={classes.sectionSubtitle}>
                  Title, category, and location details
                </Typography>

                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <FormField label="Title" classes={classes}>
                      <TextField
                        className={classes.textField}
                        error={Boolean(touched.title && errors.title)}
                        helperText={touched.title && errors.title}
                        variant="outlined"
                        fullWidth
                        name="title"
                        type="text"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        placeholder="What experience would you like to host?"
                        defaultValue={experience?.title}
                      />
                    </FormField>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormField label="Category" classes={classes}>
                      <FormControl variant="outlined" fullWidth>
                        <Select
                          className={classes.selectRoot}
                          inputProps={{ className: classes.selectInput }}
                          onChange={(e) =>
                            setFormState({
                              ...formState,
                              experience_category: e.target.value,
                            })
                          }
                          defaultValue={experience?.experience_category}
                          displayEmpty
                        >
                          <MenuItem value="" disabled>
                            Select category
                          </MenuItem>
                          {options.map((item) => (
                            <MenuItem key={item} value={item}>
                              {item}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </FormField>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormField label="Country" classes={classes}>
                      <FormControl variant="outlined" fullWidth>
                        <Select
                          className={classes.selectRoot}
                          inputProps={{ className: classes.selectInput }}
                          onChange={(e) =>
                            setFormState({
                              ...formState,
                              country: e.target.value,
                            })
                          }
                          defaultValue={experience?.country}
                          displayEmpty
                        >
                          <MenuItem value="" disabled>
                            Select country
                          </MenuItem>
                          {COUNTRIES.map((item) => (
                            <MenuItem key={item} value={item}>
                              {item}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </FormField>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormField label="District / City" classes={classes}>
                      <TextField
                        className={classes.textField}
                        error={Boolean(touched.district && errors.district)}
                        helperText={touched.district && errors.district}
                        variant="outlined"
                        fullWidth
                        name="district"
                        type="text"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        placeholder="District or city"
                        defaultValue={experience?.district}
                      />
                    </FormField>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormField label="Exact location" classes={classes}>
                      <Box className={classes.placesWrap}>
                        <PlacesAutocomplete
                          value={address}
                          onChange={setLocation}
                          onSelect={setLocation}
                        >
                          {({
                            getInputProps,
                            suggestions,
                            getSuggestionItemProps,
                            loading,
                          }) => (
                            <>
                              <input
                                {...getInputProps({
                                  placeholder: "Search for a place",
                                  className: classes.placesInput,
                                })}
                              />
                              {(loading || suggestions.length > 0) && (
                                <Box className={classes.suggestionsList}>
                                  {loading && (
                                    <Box className={classes.suggestion}>
                                      Searching…
                                    </Box>
                                  )}
                                  {suggestions.map((suggestion) => (
                                    <Box
                                      {...getSuggestionItemProps(suggestion, {
                                        className: classes.suggestion,
                                        style: {
                                          backgroundColor: suggestion.active
                                            ? TOKENS.bg
                                            : TOKENS.surface,
                                        },
                                      })}
                                    >
                                      <LocationCityIcon
                                        style={{
                                          marginRight: 10,
                                          color: TOKENS.textMuted,
                                          fontSize: 18,
                                        }}
                                      />
                                      <Typography variant="inherit">
                                        {suggestion.description}
                                      </Typography>
                                    </Box>
                                  ))}
                                </Box>
                              )}
                            </>
                          )}
                        </PlacesAutocomplete>
                      </Box>
                    </FormField>
                  </Grid>

                  <Grid item xs={12}>
                    <FormField
                      label="Languages"
                      helper="Languages you host in (e.g. English, Swahili)"
                      classes={classes}
                    >
                      <MultiItemsInput
                        handleAddItem={handleAddLanguage}
                        handleDeleteItem={handleDeleteLanguage}
                        itemsList={languages}
                        currentItem={currentLanguage}
                        handleCurrenItemChange={(e) =>
                          setCurrentLanguage(e.target.value)
                        }
                        placeholder="Type a language and press Enter"
                      />
                    </FormField>
                  </Grid>
                </Grid>
              </Box>

              {/* ─── Description ─────────────────────────────────────── */}
              <Box className={classes.sectionCard}>
                <Typography className={classes.sectionTitle}>
                  Description
                </Typography>
                <Typography className={classes.sectionSubtitle}>
                  Tell guests about the experience
                </Typography>

                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <FormField label="Full description" classes={classes}>
                      <TextField
                        className={classes.textField}
                        error={Boolean(
                          touched.description && errors.description,
                        )}
                        helperText={touched.description && errors.description}
                        variant="outlined"
                        fullWidth
                        multiline
                        minRows={4}
                        name="description"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        placeholder="Inspire guests to take your experience. Talk about the details of the itinerary you've planned."
                        defaultValue={experience?.description}
                      />
                    </FormField>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormField label="Brief description" classes={classes}>
                      <TextField
                        className={classes.textField}
                        error={Boolean(
                          touched.brief_description && errors.brief_description,
                        )}
                        helperText={
                          touched.brief_description && errors.brief_description
                        }
                        variant="outlined"
                        fullWidth
                        name="brief_description"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        placeholder="Short, descriptive, and exciting"
                        defaultValue={experience?.brief_description}
                      />
                    </FormField>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormField
                      label="What guests need to know"
                      classes={classes}
                    >
                      <TextField
                        className={classes.textField}
                        error={Boolean(
                          touched.what_guests_need_to_know &&
                          errors.what_guests_need_to_know,
                        )}
                        helperText={
                          touched.what_guests_need_to_know &&
                          errors.what_guests_need_to_know
                        }
                        variant="outlined"
                        fullWidth
                        name="what_guests_need_to_know"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        placeholder="ID, umbrella, hiking shoes, etc."
                        defaultValue={experience?.what_guests_need_to_know}
                      />
                    </FormField>
                  </Grid>

                  <Grid item xs={12}>
                    <FormField
                      label="What you provide"
                      helper="Items or services included in the experience"
                      classes={classes}
                    >
                      <MultiItemsInput
                        handleAddItem={handleAddProvision}
                        handleDeleteItem={handleDeleteProvision}
                        itemsList={provisions}
                        currentItem={currentProvision}
                        handleCurrenItemChange={(e) =>
                          setCurrentProvision(e.target.value)
                        }
                        placeholder="Type an item and press Enter"
                      />
                    </FormField>
                  </Grid>
                </Grid>
              </Box>

              {/* ─── Pricing ─────────────────────────────────────────── */}
              <Box className={classes.sectionCard}>
                <Typography className={classes.sectionTitle}>
                  Pricing
                </Typography>
                <Typography className={classes.sectionSubtitle}>
                  Base price, group size, and price variants
                </Typography>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <FormField label="Base price (USD)" classes={classes}>
                      <TextField
                        className={classes.textField}
                        error={Boolean(touched.price && errors.price)}
                        helperText={touched.price && errors.price}
                        variant="outlined"
                        fullWidth
                        name="price"
                        type="number"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        defaultValue={experience?.price}
                        placeholder="0.00"
                      />
                    </FormField>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormField
                      label="Min group size"
                      helper="Optional"
                      classes={classes}
                    >
                      <TextField
                        className={classes.textField}
                        variant="outlined"
                        fullWidth
                        name="minGroupSize"
                        type="number"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        defaultValue={experience?.minGroupSize}
                        placeholder="1"
                      />
                    </FormField>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormField
                      label="Max group size"
                      helper="Optional"
                      classes={classes}
                    >
                      <TextField
                        className={classes.textField}
                        variant="outlined"
                        fullWidth
                        name="maxGroupSize"
                        type="number"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        defaultValue={experience?.maxGroupSize}
                        placeholder="1000"
                      />
                    </FormField>
                  </Grid>
                </Grid>

                <Box style={{ marginTop: 24 }}>
                  <Typography
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: TOKENS.textPrimary,
                      marginBottom: 4,
                    }}
                  >
                    Price variants
                  </Typography>
                  <Typography
                    style={{
                      fontSize: 12,
                      color: TOKENS.textMuted,
                      marginBottom: 8,
                    }}
                  >
                    Different price tiers for adults, children, or groups
                  </Typography>

                  {variant.map((item, idx) => (
                    <Box key={idx} className={classes.variantCard}>
                      <Box className={classes.variantHeader}>
                        <Typography className={classes.variantLabel}>
                          Variant #{idx + 1}
                        </Typography>
                        {variant.length > 1 && (
                          <Button
                            className={classes.removeVariantBtn}
                            onClick={() => removeFormFields(idx)}
                            startIcon={<Cancel style={{ fontSize: 14 }} />}
                          >
                            Remove
                          </Button>
                        )}
                      </Box>
                      <Grid container spacing={2}>
                        <Grid
                          item
                          xs={12}
                          md={item.category === "Group" ? 4 : 6}
                        >
                          <FormField label="Category" classes={classes}>
                            <FormControl variant="outlined" fullWidth>
                              <Select
                                className={classes.selectRoot}
                                inputProps={{ className: classes.selectInput }}
                                onChange={(e) => handleChangeVariant(idx, e)}
                                name="category"
                                defaultValue={item.category}
                              >
                                {["Adult", "Children", "Group"].map((c) => (
                                  <MenuItem key={c} value={c}>
                                    {c}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </FormField>
                        </Grid>
                        {item.category === "Group" && (
                          <Grid item xs={12} md={4}>
                            <FormField
                              label="Number of people"
                              classes={classes}
                            >
                              <TextField
                                className={classes.textField}
                                variant="outlined"
                                fullWidth
                                name="number_of_people"
                                type="number"
                                onChange={(e) => handleChangeVariant(idx, e)}
                                defaultValue={item.number_of_people}
                              />
                            </FormField>
                          </Grid>
                        )}
                        <Grid
                          item
                          xs={12}
                          md={item.category === "Group" ? 4 : 6}
                        >
                          <FormField
                            label={
                              item.category === "Children"
                                ? "Price for children 2-12 (USD)"
                                : "Price (USD)"
                            }
                            classes={classes}
                          >
                            <TextField
                              className={classes.textField}
                              variant="outlined"
                              fullWidth
                              name="price"
                              type="number"
                              defaultValue={item.price}
                              onChange={(e) => handleChangeVariant(idx, e)}
                            />
                          </FormField>
                        </Grid>
                      </Grid>
                    </Box>
                  ))}

                  {variant.length < 3 && (
                    <Button
                      variant="outlined"
                      className={classes.addVariantBtn}
                      startIcon={<Add />}
                      onClick={addFormFields}
                    >
                      Add price variant
                    </Button>
                  )}
                </Box>
              </Box>

              {/* ─── Availability ────────────────────────────────────── */}
              <Box className={classes.sectionCard}>
                <Typography className={classes.sectionTitle}>
                  Availability
                </Typography>
                <Typography className={classes.sectionSubtitle}>
                  Select dates when this experience is booked or unavailable
                </Typography>
                <SingleDatePicker
                  placeholder="Select dates"
                  block={false}
                  numberOfMonths={1}
                  onDateChange={handleDateChange}
                  focused={formState.calendarFocused}
                  onFocusChange={() =>
                    setFormState({
                      ...formState,
                      calendarFocused: !formState.calendarFocused,
                    })
                  }
                  keepOpenOnDateSelect={true}
                  isDayHighlighted={(d1) =>
                    formState.dates.some((d2) => d1.isSame(d2, "day"))
                  }
                />
                {formState.dates?.length > 0 && (
                  <Box
                    style={{
                      marginTop: 16,
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 8,
                    }}
                  >
                    {formState.dates.map((d, i) => (
                      <Chip
                        key={i}
                        label={moment(d).format("MMM D, YYYY")}
                        onDelete={() => handleDateChange(d)}
                        style={{
                          backgroundColor: "#F2F2F2",
                          color: TOKENS.accent,
                          fontWeight: 500,
                        }}
                      />
                    ))}
                  </Box>
                )}
              </Box>

              {/* ─── Sticky save bar ─────────────────────────────────── */}
              <Box className={classes.stickyBar}>
                <Button
                  variant="outlined"
                  className={classes.cancelBtn}
                  onClick={() => navigate(-1)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className={classes.primaryBtn}
                  disabled={isSubmitting}
                >
                  Update Experience
                </Button>
              </Box>
            </form>
          )}
        </Formik>
      </Container>
    </Box>
  );
}

const mapStateToProps = (state) => ({
  experience: state.fetchedExp.experience,
});

export default connect(mapStateToProps, { editExperience })(EditExperience);
